import React, { useContext } from 'react';
import { useTranslation } from 'react-i18next';
import { DialogShell, DialogHeader, DialogFooter, dialogButtonClass, StatusDot } from '@redpanda/ui';
import { toIntlLocale } from '@redpanda/utils';
import { Payroll } from '../stores/payrollStore';
import { UserContext } from '../context/UserContext';
import { payrollTone, toneOf } from '../utils/statusTone';

interface PayslipDialogProps {
  payroll: Payroll;
  onClose: () => void;
}

const INCOME = ['baseSalary', 'allowances', 'bonuses', 'overtimePay'] as const;
const DEDUCTIONS = ['laborInsurance', 'healthInsurance', 'laborPension', 'otherDeductions'] as const;

/**
 * 薪資單：直接用列表那一筆的資料顯示（原本另外打一支 API 還快取在記憶體，改過金額再打開會看到舊的）
 * 列印（§3-16）：只印 .payslip-print 區塊，強制白底黑字，按鈕與側欄不印（index.css @media print）
 */
const PayslipDialog: React.FC<PayslipDialogProps> = ({ payroll, onClose }) => {
  const { t, i18n } = useTranslation(['pages', 'translation']);
  const { user } = useContext(UserContext)!;
  const locale = toIntlLocale(i18n.language);
  const money = (value: number) => `$${Math.round(value).toLocaleString(locale)}`;

  const totalIncome = INCOME.reduce((s, f) => s + (payroll[f] || 0), 0);
  const totalDeductions = DEDUCTIONS.reduce((s, f) => s + (payroll[f] || 0), 0);
  const period = t('pages:payroll.payslip.periodValue', { year: payroll.periodYear, month: payroll.periodMonth });

  const section = (title: string, fields: readonly (typeof INCOME[number] | typeof DEDUCTIONS[number])[], total: number, totalLabel: string) => (
    <section>
      <h3 className="text-sm font-semibold text-fg mb-2">{title}</h3>
      <dl className="divide-y divide-line border-y border-line text-sm">
        {fields.filter(f => f === 'baseSalary' || payroll[f] > 0).map(field => (
          <div key={field} className="flex justify-between py-2">
            <dt className="text-fg-secondary">{t(`pages:payroll.form.${field}`)}</dt>
            <dd className="tabular-nums text-fg">{money(payroll[field])}</dd>
          </div>
        ))}
        <div className="flex justify-between py-2 font-medium text-fg">
          <dt>{totalLabel}</dt>
          <dd className="tabular-nums">{money(total)}</dd>
        </div>
      </dl>
    </section>
  );

  return (
    <DialogShell onClose={onClose} className="max-w-2xl">
      <DialogHeader title={t('pages:payroll.payslip.title')} onClose={onClose} closeLabel={t('translation:common.actions.close')} />
      <div className="payslip-print p-6 space-y-6">
        {/* 列印時的頁首：公司名稱＋報表名稱＋期間 */}
        <header className="flex items-baseline justify-between gap-4 border-b border-line pb-4">
          <div>
            {user?.organization?.name && <p className="text-[13px] text-fg-secondary">{user.organization.name}</p>}
            {/* 畫面上對話框標題已經是「薪資單」，報表名稱只在列印時出現 */}
            <p className="text-lg font-semibold text-fg"><span className="hidden print:inline">{t('pages:payroll.payslip.title')}・</span>{period}</p>
          </div>
          <span className="no-print"><StatusDot tone={toneOf(payrollTone, payroll.status)} label={t(`pages:payroll.status.${payroll.status}`)} /></span>
        </header>

        <dl className="grid grid-cols-2 gap-x-6 gap-y-3 text-sm">
          <div>
            <dt className="text-[13px] text-fg-secondary">{t('pages:payroll.payslip.employeeName')}</dt>
            <dd className="font-medium text-fg">{payroll.employee?.name || '—'}</dd>
          </div>
          <div>
            <dt className="text-[13px] text-fg-secondary">{t('pages:payroll.payslip.employeeNumber')}</dt>
            <dd className="font-medium text-fg tabular-nums break-all">{payroll.employee?.employeeNumber || '—'}</dd>
          </div>
          {payroll.employee?.department && (
            <div>
              <dt className="text-[13px] text-fg-secondary">{t('translation:common.table.columns.department')}</dt>
              <dd className="font-medium text-fg">{payroll.employee.department}</dd>
            </div>
          )}
          {payroll.paidAt && (
            <div>
              <dt className="text-[13px] text-fg-secondary">{t('pages:payroll.payslip.paidAt')}</dt>
              <dd className="font-medium text-fg tabular-nums">{new Date(payroll.paidAt).toLocaleDateString(locale)}</dd>
            </div>
          )}
        </dl>

        <div className="grid grid-cols-1 sm:grid-cols-2 gap-6 print-two-columns">
          {section(t('pages:payroll.form.incomeSection'), INCOME, totalIncome, t('pages:payroll.form.totalIncome'))}
          {section(t('pages:payroll.form.deductionsSection'), DEDUCTIONS, totalDeductions, t('pages:payroll.form.totalDeductions'))}
        </div>

        <div className="flex items-baseline justify-between border-t-2 border-fg pt-3">
          <span className="text-base font-semibold text-fg">{t('pages:payroll.form.netSalary')}</span>
          <span className="text-2xl font-semibold text-fg tabular-nums">{money(payroll.netSalary)}</span>
        </div>

        {/* 勞退公司提繳不從薪資扣，薪資單上列出供員工核對（一般薪資單的寫法） */}
        {!!payroll.employerPension && (
          <p className="flex justify-between text-sm text-fg-secondary">
            <span>{t('pages:payroll.payslip.employerPension')}</span>
            <span className="tabular-nums">{money(payroll.employerPension)}</span>
          </p>
        )}

        {payroll.notes && (
          <div className="text-sm">
            <p className="text-[13px] text-fg-secondary mb-1">{t('pages:payroll.form.notes')}</p>
            <p className="text-fg whitespace-pre-wrap">{payroll.notes}</p>
          </div>
        )}
      </div>
      <DialogFooter>
        <button type="button" onClick={onClose} className={dialogButtonClass.cancel}>
          {t('translation:common.actions.close')}
        </button>
        <button type="button" onClick={() => window.print()} className={dialogButtonClass.primary}>
          {t('pages:payroll.payslip.print')}
        </button>
      </DialogFooter>
    </DialogShell>
  );
};

export default PayslipDialog;
